// Quota and Tier Management API module
import { BaseApiClient } from './BaseApiClient';
import { configService } from '../config';
import type {
  ApiResponse,
} from '@/types/api';
import type {
  QuotaConfiguration,
  QuotaLimits,
  QuotaUpdateRequest,
  TierPricing,
  TierLocalizedVariants,
} from '@/types/quota';

export class QuotaTierApi extends BaseApiClient {
  constructor() {
    super('QuotaTierAPI');
  }

  async getQuotas(): Promise<ApiResponse<QuotaConfiguration[]>> {
    this.logger.info('getQuotas: Making request to GET /quotas');
    const response = await this.api.get('/quotas');
    this.logger.info('getQuotas: Raw response received', {
      status: response.status,
      data: response.data,
      count: response.data?.configurations?.length || response.data?.length || 0
    });

    // Handle potential response structure variations
    if (response.data && Array.isArray(response.data)) {
      // Direct array response
      return {
        data: response.data,
        message: 'Quotas retrieved successfully',
        status: 'success' as const,
        timestamp: new Date().toISOString()
      };
    } else if (response.data && response.data.configurations && Array.isArray(response.data.configurations)) {
      // Server returns: { configurations: [...], total_count: N }
      this.logger.debug(`Found quota configurations`, { count: response.data.configurations.length });
      return {
        data: response.data.configurations,
        message: 'Quotas retrieved successfully',
        status: 'success' as const,
        timestamp: new Date().toISOString()
      };
    } else if (response.data && response.data.quotas && Array.isArray(response.data.quotas)) {
      // Nested under quotas field
      return {
        data: response.data.quotas,
        message: 'Quotas retrieved successfully',
        status: 'success' as const,
        timestamp: new Date().toISOString()
      };
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      // Already in correct format - assume it matches ApiResponse structure
      return response.data;
    } else {
      this.logger.warn('Unexpected response format for GET /quotas:', response.data);
      return {
        data: [],
        message: 'No quotas found',
        status: 'success' as const,
        timestamp: new Date().toISOString()
      };
    }
  }

  async getQuotaForTier(tier: string): Promise<ApiResponse<QuotaConfiguration>> {
    // Validate tier parameter
    if (!tier || typeof tier !== 'string') {
      throw new Error('Invalid tier parameter');
    }
    if (!/^[a-z0-9_-]+$/i.test(tier)) {
      throw new Error('Tier name can only contain letters, numbers, hyphens, and underscores');
    }
    if (tier.length > 50) {
      throw new Error('Tier name must be 50 characters or less');
    }

    const response = await this.api.get<ApiResponse<QuotaConfiguration>>(`/quotas/${tier}`);
    return response.data;
  }

  async updateQuotaForTier(
    tier: string,
    limits: QuotaLimits,
    displayName?: string,
    isPublic?: boolean,
    pricing?: TierPricing[],
    displayOrder?: number,
    description?: string,
    features?: string[],
    localizedVariants?: TierLocalizedVariants,
    priorityDelayMinSeconds?: number,
    priorityDelayMaxSeconds?: number
  ): Promise<ApiResponse<QuotaConfiguration>> {
    // Validate tier parameter to prevent injection attacks
    if (!tier || typeof tier !== 'string') {
      throw new Error('Invalid tier parameter');
    }
    if (!/^[a-z0-9_-]+$/i.test(tier)) {
      throw new Error('Tier name can only contain letters, numbers, hyphens, and underscores');
    }
    if (tier.length > 50) {
      throw new Error('Tier name must be 50 characters or less');
    }

    // Validate limits structure
    if (!limits || typeof limits !== 'object') {
      throw new Error('Invalid limits parameter');
    }

    // Validate numeric limits
    const numericFields: Array<keyof QuotaLimits> = [
      'max_images_per_day',
      'max_videos_per_day',
      'max_input_tokens_per_day',
      'max_output_tokens_per_day',
      'max_input_bytes_per_day',
      'max_output_bytes_per_day'
    ];

    for (const field of numericFields) {
      const value = limits[field];
      if (value !== undefined && (typeof value !== 'number' || value < 0 || !Number.isFinite(value))) {
        throw new Error(`Invalid value for ${field}: must be a positive number`);
      }
    }

    // Validate display name
    if (displayName !== undefined && (typeof displayName !== 'string' || displayName.length > 100)) {
      throw new Error('Display name must be a string with 100 characters or less');
    }

    // Validate description
    if (description !== undefined && (typeof description !== 'string' || description.length > 500)) {
      throw new Error('Description must be a string with 500 characters or less');
    }

    // Validate display order
    if (displayOrder !== undefined && (typeof displayOrder !== 'number' || displayOrder < 1 || displayOrder > 999)) {
      throw new Error('Display order must be a number between 1 and 999');
    }

    // Validate pricing structure
    if (pricing !== undefined && Array.isArray(pricing)) {
      for (const tierPricing of pricing) {
        if (!tierPricing.currency || !['USD', 'KRW'].includes(tierPricing.currency)) {
          throw new Error('Invalid currency in pricing');
        }
        if (typeof tierPricing.monthly_price !== 'number' || tierPricing.monthly_price < 0) {
          throw new Error('Monthly price must be a positive number');
        }
      }
    }

    // Validate features
    if (features !== undefined && (!Array.isArray(features) || features.some(f => typeof f !== 'string'))) {
      throw new Error('Features must be an array of strings');
    }

    // Validate localized variants
    if (localizedVariants !== undefined && (typeof localizedVariants !== 'object' || Array.isArray(localizedVariants))) {
      throw new Error('Localized variants must be an object keyed by language code');
    }

    // Validate priority delay range (0–600s, min<=max). If one provided, require both.
    const hasDelayMin = priorityDelayMinSeconds !== undefined && priorityDelayMinSeconds !== null;
    const hasDelayMax = priorityDelayMaxSeconds !== undefined && priorityDelayMaxSeconds !== null;
    if (hasDelayMin || hasDelayMax) {
      if (!hasDelayMin || !hasDelayMax) {
        throw new Error('Both priority delay min and max seconds must be provided together');
      }
      if (typeof priorityDelayMinSeconds !== 'number' || typeof priorityDelayMaxSeconds !== 'number') {
        throw new Error('Priority delay min/max must be numbers');
      }
      if (!Number.isFinite(priorityDelayMinSeconds) || !Number.isFinite(priorityDelayMaxSeconds)) {
        throw new Error('Priority delay min/max must be finite numbers');
      }
      if (priorityDelayMinSeconds < 0 || priorityDelayMinSeconds > 600 || priorityDelayMaxSeconds < 0 || priorityDelayMaxSeconds > 600) {
        throw new Error('Priority delay seconds must be between 0 and 600');
      }
      if (priorityDelayMinSeconds > priorityDelayMaxSeconds) {
        throw new Error('Priority delay min seconds must be <= max seconds');
      }
    }

    const payload: QuotaUpdateRequest = { limits };
    if (displayName) {
      payload.display_name = displayName;
    }
    if (description !== undefined) {
      payload.description = description;
    }
    if (displayOrder !== undefined) {
      payload.display_order = displayOrder;
    }
    if (isPublic !== undefined) {
      payload.is_public = isPublic;
    }
    if (pricing !== undefined) {
      payload.pricing = pricing;
    }
    if (features !== undefined) {
      payload.features = features;
    }
    if (localizedVariants !== undefined) {
      payload.localized_variants = localizedVariants;
    }
    if (hasDelayMin && hasDelayMax) {
      payload.priority_delay_min_seconds = priorityDelayMinSeconds;
      payload.priority_delay_max_seconds = priorityDelayMaxSeconds;
    }
    this.logger.debug(`PUT /quotas/${tier}`, { tier, payloadSize: JSON.stringify(payload).length });
    const response = await this.api.put(`/quotas/${tier}`, payload);
    this.logger.debug(`Response for PUT /quotas/${tier}`, { success: !!response.data?.message });

    // Handle the actual server response structure
    // Server returns: { message: "...", configuration: { tier, display_name, limits, updated_at, etc } }
    // Frontend expects: { success: true, data: { tier, display_name, limits, updated_at, etc } }
    const serverResponse = response.data;

    if (serverResponse && serverResponse.configuration) {
      return {
        data: serverResponse.configuration,
        message: serverResponse.message || 'Quota updated successfully',
        status: 'success',
        timestamp: new Date().toISOString(),
      };
    } else {
      this.logger.warn(`Unexpected response format for PUT /quotas/${tier}:`, serverResponse);
      throw new Error(`Invalid response format from server for quota update`);
    }
  }

  // Create new quota configuration for a custom tier
  async createQuotaForTier(
    tier: string,
    limits: QuotaLimits,
    displayName?: string,
    isPublic?: boolean,
    pricing?: TierPricing[],
    displayOrder?: number,
    description?: string,
    features?: string[],
    localizedVariants?: TierLocalizedVariants,
    priorityDelayMinSeconds?: number,
    priorityDelayMaxSeconds?: number
  ): Promise<ApiResponse<QuotaConfiguration>> {
    // Reuse validation logic from updateQuotaForTier
    if (!tier || typeof tier !== 'string') {
      throw new Error('Invalid tier parameter');
    }
    if (!/^[a-z0-9_-]+$/i.test(tier)) {
      throw new Error('Tier name can only contain letters, numbers, hyphens, and underscores');
    }
    if (tier.length > 50) {
      throw new Error('Tier name must be 50 characters or less');
    }

    if (!limits || typeof limits !== 'object') {
      throw new Error('Invalid limits parameter');
    }

    const numericFields: Array<keyof QuotaLimits> = [
      'max_images_per_day',
      'max_videos_per_day',
      'max_input_tokens_per_day',
      'max_output_tokens_per_day',
      'max_input_bytes_per_day',
      'max_output_bytes_per_day'
    ];

    for (const field of numericFields) {
      const value = limits[field];
      if (value !== undefined && (typeof value !== 'number' || value < 0 || !Number.isFinite(value))) {
        throw new Error(`Invalid value for ${field}: must be a positive number`);
      }
    }

    if (displayName !== undefined && (typeof displayName !== 'string' || displayName.length > 100)) {
      throw new Error('Display name must be a string with 100 characters or less');
    }

    if (description !== undefined && (typeof description !== 'string' || description.length > 500)) {
      throw new Error('Description must be a string with 500 characters or less');
    }

    if (displayOrder !== undefined && (typeof displayOrder !== 'number' || displayOrder < 1 || displayOrder > 999)) {
      throw new Error('Display order must be a number between 1 and 999');
    }

    if (pricing !== undefined && Array.isArray(pricing)) {
      for (const tierPricing of pricing) {
        if (!tierPricing.currency || !['USD', 'KRW'].includes(tierPricing.currency)) {
          throw new Error('Invalid currency in pricing');
        }
        if (typeof tierPricing.monthly_price !== 'number' || tierPricing.monthly_price < 0) {
          throw new Error('Monthly price must be a positive number');
        }
      }
    }

    // Validate features
    if (features !== undefined && (!Array.isArray(features) || features.some(f => typeof f !== 'string'))) {
      throw new Error('Features must be an array of strings');
    }

    // Validate localized variants
    if (localizedVariants !== undefined && (typeof localizedVariants !== 'object' || Array.isArray(localizedVariants))) {
      throw new Error('Localized variants must be an object keyed by language code');
    }

    // Validate priority delay range (0–600s, min<=max). If one provided, require both.
    const hasDelayMin = priorityDelayMinSeconds !== undefined && priorityDelayMinSeconds !== null;
    const hasDelayMax = priorityDelayMaxSeconds !== undefined && priorityDelayMaxSeconds !== null;
    if (hasDelayMin || hasDelayMax) {
      if (!hasDelayMin || !hasDelayMax) {
        throw new Error('Both priority delay min and max seconds must be provided together');
      }
      if (typeof priorityDelayMinSeconds !== 'number' || typeof priorityDelayMaxSeconds !== 'number') {
        throw new Error('Priority delay min/max must be numbers');
      }
      if (!Number.isFinite(priorityDelayMinSeconds) || !Number.isFinite(priorityDelayMaxSeconds)) {
        throw new Error('Priority delay min/max must be finite numbers');
      }
      if (priorityDelayMinSeconds < 0 || priorityDelayMinSeconds > 600 || priorityDelayMaxSeconds < 0 || priorityDelayMaxSeconds > 600) {
        throw new Error('Priority delay seconds must be between 0 and 600');
      }
      if (priorityDelayMinSeconds > priorityDelayMaxSeconds) {
        throw new Error('Priority delay min seconds must be <= max seconds');
      }
    }

    const payload: QuotaUpdateRequest = { limits };
    if (displayName) {
      payload.display_name = displayName;
    }
    if (description !== undefined) {
      payload.description = description;
    }
    if (displayOrder !== undefined) {
      payload.display_order = displayOrder;
    }
    if (isPublic !== undefined) {
      payload.is_public = isPublic;
    }
    if (pricing !== undefined) {
      payload.pricing = pricing;
    }
    if (features !== undefined) {
      payload.features = features;
    }
    if (localizedVariants !== undefined) {
      payload.localized_variants = localizedVariants;
    }
    if (hasDelayMin && hasDelayMax) {
      payload.priority_delay_min_seconds = priorityDelayMinSeconds;
      payload.priority_delay_max_seconds = priorityDelayMaxSeconds;
    }
    this.logger.debug(`POST /quotas/${tier}`, { tier, payloadSize: JSON.stringify(payload).length });
    const response = await this.api.post(`/quotas/${tier}`, payload);
    this.logger.debug(`Response for POST /quotas/${tier}`, { success: !!response.data?.message });

    // Handle the actual server response structure
    // Server returns: { message: "...", configuration: { tier, display_name, limits, created_at, etc } }
    // Frontend expects: { success: true, data: { tier, display_name, limits, created_at, etc } }
    const serverResponse = response.data;

    if (serverResponse && serverResponse.configuration) {
      return {
        data: serverResponse.configuration,
        message: serverResponse.message || 'Quota created successfully',
        status: 'success',
        timestamp: new Date().toISOString(),
      };
    } else {
      this.logger.warn(`Unexpected response format for POST /quotas/${tier}:`, serverResponse);
      throw new Error(`Invalid response format from server for quota creation`);
    }
  }

  async deleteQuotaForTier(tier: string): Promise<ApiResponse<void>> {
    // Validate tier parameter
    if (!tier || typeof tier !== 'string') {
      throw new Error('Invalid tier parameter');
    }
    if (!/^[a-z0-9_-]+$/i.test(tier)) {
      throw new Error('Tier name can only contain letters, numbers, hyphens, and underscores');
    }
    if (tier.length > 50) {
      throw new Error('Tier name must be 50 characters or less');
    }

    // Prevent deletion of core tiers
    if (['free', 'pro'].includes(tier.toLowerCase())) {
      throw new Error('Cannot delete core system tiers (free, pro)');
    }

    const response = await this.api.delete<ApiResponse<void>>(`/quotas/${tier}`);
    return response.data;
  }

  async initializeDefaultQuotas(): Promise<ApiResponse<{ message: string; created_count: number }>> {
    const response = await this.api.post<ApiResponse<{ message: string; created_count: number }>>('/quotas/initialize-defaults');
    return response.data;
  }

  /**
   * Diagnose quota configuration issues - utility for debugging validation errors
   * This function performs client-side validation checks to help identify potential
   * issues before they cause 422 errors from the server.
   */
  async diagnoseQuotaValidation(tier: string, payload: QuotaUpdateRequest): Promise<{
    tier: string;
    environment: string;
    validationChecks: Array<{ check: string; status: 'pass' | 'fail' | 'warning'; details?: string }>;
    suggestions: string[];
  }> {
    const checks: Array<{ check: string; status: 'pass' | 'fail' | 'warning'; details?: string }> = [];
    const suggestions: string[] = [];

    // Client-side validation checks
    if (!payload.limits) {
      checks.push({ check: 'Limits object exists', status: 'fail', details: 'Missing limits object' });
      suggestions.push('Ensure limits object is provided in payload');
    } else {
      checks.push({ check: 'Limits object exists', status: 'pass' });

      // Check required numeric fields
      const requiredLimits: Array<keyof QuotaLimits> = [
        'max_images_per_day',
        'max_videos_per_day',
        'max_input_bytes_per_day',
        'max_output_bytes_per_day',
      ];
      for (const field of requiredLimits) {
        const value = payload.limits[field];
        if (value === undefined || value === null) {
          checks.push({ check: `${field} defined`, status: 'fail', details: `${field} is undefined` });
          suggestions.push(`Set ${field} to a valid number (0 or greater)`);
        } else if (typeof value !== 'number' || value < 0) {
          checks.push({ check: `${field} valid`, status: 'fail', details: `${field} must be a non-negative number` });
          suggestions.push(`Set ${field} to a valid number (0 or greater)`);
        } else {
          checks.push({ check: `${field} valid`, status: 'pass' });
        }
      }
    }

    // Check display_name requirement for production
    if (configService.isProduction) {
      if (!payload.display_name || typeof payload.display_name !== 'string' || payload.display_name.trim() === '') {
        checks.push({ check: 'Display name (production)', status: 'fail', details: 'Display name required in production' });
        suggestions.push('Provide a display_name field for production environment');
      } else {
        checks.push({ check: 'Display name (production)', status: 'pass' });
      }
    }

    // Check pricing array structure
    if (payload.pricing && Array.isArray(payload.pricing)) {
      if (payload.pricing.length === 0) {
        checks.push({ check: 'Pricing array', status: 'warning', details: 'Empty pricing array' });
        suggestions.push('Consider adding pricing information for the tier');
      } else {
        let validPricing = true;
        for (const price of payload.pricing) {
          if (!price.currency || !['USD', 'KRW'].includes(price.currency)) {
            validPricing = false;
            break;
          }
          if (typeof price.monthly_price !== 'number' || price.monthly_price < 0) {
            validPricing = false;
            break;
          }
        }
        checks.push({
          check: 'Pricing array structure',
          status: validPricing ? 'pass' : 'fail',
          details: validPricing ? undefined : 'Invalid pricing structure'
        });
        if (!validPricing) {
          suggestions.push('Ensure pricing array has valid currency and monthly_price fields');
        }
      }
    } else {
      checks.push({ check: 'Pricing array', status: 'warning', details: 'No pricing array provided' });
    }

    // Check tier name validity
    if (!tier || !/^[a-z0-9_-]+$/i.test(tier)) {
      checks.push({ check: 'Tier name format', status: 'fail', details: 'Invalid tier name format' });
      suggestions.push('Tier name must contain only letters, numbers, hyphens, and underscores');
    } else {
      checks.push({ check: 'Tier name format', status: 'pass' });
    }

    // Environment-specific suggestions
    if (configService.isProduction) {
      suggestions.push('Production API may have stricter validation than development');
      suggestions.push('Try creating the tier with minimal required fields first');
    }

    return {
      tier,
      environment: configService.getEnvironmentDisplayName(),
      validationChecks: checks,
      suggestions
    };
  }
}

